/**
 * Anchor setup — the model behind the guided "set up anchors" journey (PD-1258).
 *
 * A faithful port of the design prototype's `lvl/anchor-wizard.jsx`, with the React
 * state stripped out: everything here is a pure function over a role list, so both
 * portals drive the same wizard and the sequencing rules are testable without a DOM.
 *
 * WHY THE SEQUENCE IS WHAT IT IS. The obvious approach — anchor the top of every
 * family — was tried and rejected: it produces a horizontal top-slice of isolated
 * senior roles with nothing underneath them, which are the *hardest* roles to
 * evaluate consistently and give no vertical calibration. The journey is therefore
 * SPINE-FIRST:
 *
 *   1. CEO            → fixes the ceiling; nothing should classify at or above it
 *   2. leadership     → anchors the executive band (ordinary anchors, not ceilings)
 *   3. one family      → top-to-bottom, so every level is checked against its
 *                        neighbours: this is what vertical calibration means
 *   4. cross-family   → weighted to populous roles; one-off roles are poor anchors
 *   5. coverage       → against the floor, see-and-acknowledge, NEVER a block
 *
 * Coverage counts only levels somebody actually sits at. A level nobody occupies is
 * not a gap, and treating it as one would make the floor unreachable by design.
 */

/** Evaluated anchors required per populated level, per track. */
export const ANCHOR_FLOOR = { standard: 2, large: 3 };
/** Registers above this many roles are treated as large/dispersed. */
export const LARGE_ORG_ROLES = 1500;

/**
 * What a single cell actually needs, given how many roles sit in it.
 *
 * The floor is a target, not a quota: a level-and-track only two roles sit at can
 * never reach a floor of three, and scoring it as a permanent gap would tell the
 * user to do something impossible. Capping the requirement at the population means
 * "every role here is an anchor" reads as met. Ported from the prototype's
 * lvlAnchNeed (lvl/anchor-wizard.jsx).
 *
 * NB: anchorFloorCoverage below deliberately does NOT apply this — it scores the
 * register against the flat floor, which is the number the wizard talks about.
 */
export function anchorNeed(floor, n) {
  return Math.min(floor, n || 0);
}

/**
 * Title seniority, for registers where no level exists to read.
 *
 * On a greenfield register NOTHING carries a level, so every band-derived signal
 * collapses to zero: the CEO list is empty, families have no ladder, and each spine
 * step has nothing to offer. Job title is then the only ordering available — Carl's
 * ask was suggestions "based on Titles (Senior…) and headcount".
 *
 * INDICATIVE, NEVER AUTHORITATIVE. Evaluation sets the real level; this only decides
 * what to show first, and anything derived from it is badged as title-derived in the
 * UI so nobody mistakes it for evidence.
 *
 * Ordered longest-match-first and FIRST MATCH WINS, which is load-bearing twice over:
 *  - "Senior Manager" must rank as a manager (6), not as "senior" (4);
 *  - the vice-president clause must sit BEFORE the chief-executive one, because
 *    `\bpresident\b` matches inside "Vice President" — without it every VP would rank
 *    as a CEO and be suggested as the ceiling, which is the one pick in this journey
 *    that is expensive to get wrong.
 */
export const ANCHOR_TITLE_RANK = [
  [/\b(apprentice|trainee|graduate|intern|junior|jnr)\b/i, 1],
  /* Deliberately before chief-executive — see the note above. Do not reorder. */
  [/\bvice president\b|\bvp\b/i, 8],
  [/\bchief executive|\bceo\b|\bgroup chief exec|\bmanaging director\b|\bpresident\b/i, 10],
  [/\b(chief|cfo|coo|cto|c-suite)\b/i, 9],
  [/\b(director|vice president|\bvp\b|global head)\b/i, 8],
  [/\bhead of\b|\bhead\b/i, 7],
  [/\b(principal|senior manager|snr manager)\b/i, 6],
  [/\b(manager|\blead\b|supervisor)\b/i, 5],
  [/\b(senior|snr)\b/i, 4],
  [/\b(specialist|analyst|officer|advisor|adviser|engineer|executive|administrator|coordinator)\b/i, 3],
  [/\b(assistant|associate|entry)\b/i, 2],
];

/** Human label per tier — the row heading when a spine is grouped by title. */
export const ANCHOR_TIER_LABEL = {
  10: 'Chief executive',
  9: 'Chief',
  8: 'Director',
  7: 'Head of',
  6: 'Principal',
  5: 'Manager',
  4: 'Senior',
  3: 'Professional',
  2: 'Assistant',
  1: 'Entry',
};

/**
 * Rank a role by its title alone. Unmatched titles rank 2, not 0 — an unrecognised
 * title is far more likely to be an ordinary individual contributor than the most
 * junior person in the business, and 0 would sort every unknown title to the bottom.
 */
export function titleRank(role) {
  const t = String(titleOf(role) || '');
  for (let i = 0; i < ANCHOR_TITLE_RANK.length; i++) {
    if (ANCHOR_TITLE_RANK[i][0].test(t)) return ANCHOR_TITLE_RANK[i][1];
  }
  return 2;
}

/**
 * Indicative headcount for a role.
 *
 * Incumbent counts aren't modelled yet, so — exactly as the prototype does — a
 * deterministic hash of the role's identity stands in where `headcount` is absent.
 * It is stable per role (so the UI doesn't flicker between renders) and skewed so
 * single-incumbent cases exist, because "is this a one-off role?" is the question
 * the scatter step actually asks. ALWAYS label it as indicative in the UI: it is a
 * placeholder for real data, not a measurement.
 */
export function anchorHeadcount(role) {
  if (!role) return 0;
  if (typeof role.headcount === 'number') return role.headcount;
  let h = 0;
  const s = String(role.id ?? role.doc_id ?? '') + String(role.ref ?? role.reference ?? '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 9973;
  return h % 7 === 0 ? 1 : 1 + (h % 12);
}

/**
 * Is this role a *confirmed* anchor candidate — fully evaluated, not classified?
 *
 * Prefers the SERVER's own verdict (`evaluated`), which getAnchorSetupData computes
 * with the exact predicate saveLfAnchor guards the write with. The local derivation
 * below is only a fallback for callers that don't supply it (control-centre rows),
 * and it is deliberately STRICTER — it also demands a resolvable grade code. That
 * extra term is what made a role completed-but-not-approved read as "needs
 * evaluation" in the wizard while the server treated it as evaluated, so wherever
 * the flag exists it wins.
 */
export function anchorEvaluated(role) {
  if (!role) return false;
  const lv = role.levelling || role;
  if (typeof role.evaluated === 'boolean') return role.evaluated;
  if (typeof lv.evaluated === 'boolean') return lv.evaluated;
  const method = lv.method || null;
  const complete = lv.status_key === 'levelled' || lv.evaluation_status === 'COMPLETED';
  return method === 'evaluate' && !!complete && !!levelCodeOf(role);
}

/** The role's grade code ("IC5"), however the host shaped the row. */
export function levelCodeOf(role) {
  if (!role) return null;
  const lv = role.levelling || role;
  return lv.level_code || lv.level || role.level || null;
}

/** Sortable band for a grade code, via the host's code map. Higher = more senior. */
function bandOf(role, codeMap) {
  const code = levelCodeOf(role);
  if (!code) return 0;
  const meta = (codeMap || {})[code];
  if (meta && typeof meta.global === 'number') return meta.global;
  const lv = role.levelling || role;
  const pos = Number(lv.level_band ?? lv.levelPosition ?? lv.position);
  return isNaN(pos) ? 0 : pos;
}

const byBandThenTitle = (codeMap) => (a, b) =>
  bandOf(b, codeMap) - bandOf(a, codeMap) ||
  String(titleOf(a)).localeCompare(String(titleOf(b)));

/** Display title, however the host shaped the row. */
export function titleOf(role) {
  return (role && (role.title || role.doc_title || role.doc_name)) || '';
}
/** Stable id, however the host shaped the row. */
export function idOf(role) {
  return role && (role.id ?? role.doc_id);
}

/**
 * Register-wide coverage against the floor.
 *
 * @param {Array}  roles    every role in the register
 * @param {Array}  newPicks [{ role }] — picks not yet saved, counted as projected
 * @param {Object} opts     { codeMap, isAnchor(role) -> bool, isPending(role) -> bool, orgSize }
 */
export function anchorFloorCoverage(roles, newPicks, opts) {
  const list = Array.isArray(roles) ? roles : [];
  const o = opts || {};
  const codeMap = o.codeMap || {};
  const isAnchor = o.isAnchor || (() => false);
  const isPending = o.isPending || (() => false);
  const orgSize = o.orgSize || (list.length > LARGE_ORG_ROLES ? 'large' : 'standard');
  const floor = ANCHOR_FLOOR[orgSize] ?? ANCHOR_FLOOR.standard;

  // Populated levels only — a level nobody sits at is not a gap.
  const byCode = {};
  for (const r of list) {
    const code = levelCodeOf(r);
    if (!code) continue;
    if (!byCode[code]) byCode[code] = { n: 0, anch: 0, proj: 0 };
    byCode[code].n++;
  }
  // A PENDING anchor is not evidence yet: it has no evaluation behind it, so it
  // cannot count towards a floor that exists to guarantee evaluated evidence.
  for (const r of list) {
    const code = levelCodeOf(r);
    if (!code || !byCode[code]) continue;
    if (isAnchor(r) && !isPending(r)) byCode[code].anch++;
  }
  for (const p of newPicks || []) {
    const code = levelCodeOf(p && p.role);
    if (code && byCode[code]) byCode[code].proj++;
  }

  const cells = Object.keys(byCode)
    .map((code) => ({ code, ...byCode[code], meta: codeMap[code] || {} }))
    .sort((a, b) => (b.meta.global || 0) - (a.meta.global || 0) || a.code.localeCompare(b.code));

  return {
    floor,
    orgSize,
    cells,
    pop: cells.length,
    metNow: cells.filter((c) => c.anch >= floor).length,
    metAfter: cells.filter((c) => c.anch + c.proj >= floor).length,
    gaps: cells.filter((c) => c.anch + c.proj < floor),
    unlevelled: list.filter((r) => !levelCodeOf(r)).length,
  };
}

/**
 * Families, each with its role list (most senior first), any existing anchor, a
 * scatter suggestion and its spine depth.
 *
 * The suggestion is the crux of the scatter step: prefer a role with MULTIPLE
 * incumbents from the top band or the one below it. A populous role is a defensible
 * reference — several people do the job, so its scope is understood — whereas a
 * one-off role is idiosyncratic. Executives are the exception, which is why the
 * leadership layer is handled by its own step rather than this rule.
 */
export function anchorFamilies(roles, opts) {
  const o = opts || {};
  const codeMap = o.codeMap || {};
  const isAnchor = o.isAnchor || (() => false);
  const familyOf = o.familyOf || ((r) => r.family);
  const groupOf = o.groupOf || ((r) => r.familyGroup);
  const sortRoles = byBandThenTitle(codeMap);

  const map = {};
  for (const r of Array.isArray(roles) ? roles : []) {
    const fam = familyOf(r);
    if (!fam) continue;
    (map[fam] = map[fam] || []).push(r);
  }

  return Object.keys(map).sort().map((family) => {
    const list = [...map[family]].sort(sortRoles);
    const existing = list.find((r) => isAnchor(r)) || null;
    const levelled = list.filter((r) => levelCodeOf(r));
    const topBand = levelled.length ? bandOf(levelled[0], codeMap) : 0;
    const nearTop = levelled.filter((r) => bandOf(r, codeMap) >= topBand - 1);
    const codes = [...new Set(levelled.map((r) => levelCodeOf(r)))].sort(
      (a, b) => ((codeMap[b] || {}).global || 0) - ((codeMap[a] || {}).global || 0),
    );

    /* Title-ranked order, used wherever no level evidence exists. */
    const ranked = [...list].sort(
      (a, b) => titleRank(b) - titleRank(a)
        || anchorHeadcount(b) - anchorHeadcount(a)
        || String(titleOf(a)).localeCompare(String(titleOf(b))),
    );

    /* Evidence first; fall back to title + headcount so a greenfield family is never
       left with nothing to accept — which is what previously made the whole step
       unpassable on a register with no levels. `suggWeak` records WHICH signal was
       used, because a title-derived suggestion has to say so in the UI rather than
       presenting itself as a finding. */
    const strong = nearTop.find((r) => anchorHeadcount(r) > 1) || levelled[0] || null;
    const weak = ranked.find((r) => anchorHeadcount(r) > 1) || ranked[0] || null;

    return {
      family,
      group: groupOf(list[0]) || 'Other',
      list,
      ranked,
      existing,
      suggested: strong || weak,
      suggWeak: !strong && !!weak,
      people: list.reduce((n, r) => n + anchorHeadcount(r), 0),
      levelledN: levelled.length,
      codes,
      depth: codes.length,
    };
  });
}

/**
 * The rows of a family's spine — one per rung of its ladder.
 *
 * Where levels exist, that's one row per populated level. Where nothing is levelled
 * there are no levels to read, so the ladder is grouped by job-title tier instead
 * (Sasha: "it should just display a list of roles in each family/function"). Without
 * this a greenfield family renders an empty spine and the step cannot be completed.
 *
 * `code` is null on a title-tier row — callers must treat it as a display grouping,
 * never as a grade.
 */
export function spineRows(family) {
  const g = family || {};
  const list = g.list || [];
  if ((g.codes || []).length) {
    return g.codes.map((code) => ({
      key: code,
      code,
      label: code,
      byTitle: false,
      at: list.filter((r) => levelCodeOf(r) === code),
    }));
  }
  const ranked = g.ranked || list;
  return [...new Set(ranked.map(titleRank))]
    .sort((a, b) => b - a)
    .map((rk) => ({
      key: `t${rk}`,
      code: null,
      label: ANCHOR_TIER_LABEL[rk] || 'Other',
      byTitle: true,
      at: ranked.filter((r) => titleRank(r) === rk),
    }));
}

/** Total indicative headcount across a family — the spine tiebreak. */
export function familyPeople(family) {
  return (family && family.list ? family.list : []).reduce((n, r) => n + anchorHeadcount(r), 0);
}

/**
 * Families ranked for the spine step: deepest ladder, then longest role list, then
 * most PEOPLE.
 *
 * Three terms, and the middle one is the greenfield fix: with nothing levelled every
 * family has `depth === 0`, so a two-term sort fell straight through to headcount and
 * could crown a family of two well-staffed roles — which cannot form a spine at all.
 * Role-list length is what stands in for ladder depth when no ladder is visible.
 *
 * NOTE — one deliberate divergence from the prototype. Its `famSort` applies the
 * role-count term at EVERY depth, but its own comments ask for two different things:
 * "the badge claims 'deepest ladder, most people', so the sort must use real headcount
 * … as the tiebreak", and then "where nothing is levelled, the longest role list then
 * headcount". Applied unconditionally, the role-count term overrides the headcount
 * tiebreak the first comment requires — a family of four one-person roles would beat
 * one of two forty-person roles at equal depth, while the badge said "most people".
 * So the term is scoped to the case its own comment describes: it breaks ties only
 * when NEITHER family has a ladder to read. Both comments then hold.
 *
 * The final tiebreak is summed headcount, not the count of levelled roles, for the
 * same reason — the badge has to be true.
 */
export function spineCandidates(families) {
  return [...(families || [])].sort(
    (a, b) => b.depth - a.depth
      || (!a.depth && !b.depth ? (b.list || []).length - (a.list || []).length : 0)
      || familyPeople(b) - familyPeople(a),
  );
}

/**
 * Is this family a plausible spine — a real ladder, or enough roles to form one?
 *
 * Two clauses because `depth` is unavailable on a greenfield register: 3+ populated
 * levels where levels exist, else 3+ roles. Used to decide whether the suggested
 * family is pre-selected at all — suggesting a two-role "spine" teaches the wrong
 * thing about what a spine is for.
 */
export function spineFits(family) {
  if (!family) return false;
  const depth = family.depth || 0;
  return depth >= 3 || (!depth && (family.list || []).length >= 3);
}

/**
 * The default pick on each rung of a family's spine: an existing anchor, else an
 * evaluated role, else a populous one, else whatever is there.
 *
 * Keyed by ROW KEY, not level code — a greenfield family's rungs are title tiers and
 * have no code. Keying by code silently produced an empty default set for exactly the
 * registers that needed the help most.
 */
export function spineDefaults(family, opts) {
  const o = opts || {};
  const isAnchor = o.isAnchor || (() => false);
  const out = {};
  for (const row of spineRows(family)) {
    const at = row.at || [];
    const pick =
      at.find((r) => isAnchor(r)) ||
      at.find((r) => anchorEvaluated(r)) ||
      at.find((r) => anchorHeadcount(r) > 1) ||
      at[0];
    out[row.key] = pick ? idOf(pick) : null;
  }
  return out;
}

/**
 * Assemble the final pick list, deduped with a deliberate precedence:
 * CEO > leadership > spine > scatter. A role chosen twice keeps the MOST specific
 * reason, so the review step can say why each anchor is there.
 */
export function assemblePicks(sel, roleById) {
  const seen = new Set();
  const out = [];
  const add = (id, src) => {
    const r = id != null && roleById[id];
    if (!r) return;
    const key = String(idOf(r));
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ role: r, src });
  };

  add(sel.ceoId, 'ceo');
  for (const id of sel.leadershipIds || []) add(id, 'leadership');
  for (const fam of sel.spineFamilies || []) {
    for (const id of Object.values((sel.spineSel || {})[fam] || {})) add(id, 'spine');
  }
  for (const [fam, id] of Object.entries(sel.scatterSel || {})) {
    if ((sel.spineFamilies || []).includes(fam)) continue;
    add(id, 'scatter');
  }
  return out;
}

/** How many picks came from each step — the review step's summary. */
export function pickCountsBySource(picks) {
  return (picks || []).reduce((acc, p) => {
    acc[p.src] = (acc[p.src] || 0) + 1;
    return acc;
  }, {});
}
